import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router'
import { Outlet } from 'react-router-dom'

import { env } from 'src/config/env'
import { paths } from 'src/config/paths'
import AuthSidebar from 'src/features/auth/components/sidebar'
import { queries } from 'src/lib/query-keys'
import { Logo } from 'src/shared/components/icons/logos/logo'
import { Button } from 'src/shared/components/ui/buttons/button'
import { useAuthState } from 'src/shared/features/auth/api/use-auth-state'
import { SupportDialog } from 'src/shared/features/support/components/support-dialog'
import { useIsMobile } from 'src/shared/hooks/use-mobile'
import { cn } from 'src/shared/utils/cn'

export default function AuthLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const isMobile = useIsMobile()
  const queryClient = useQueryClient()
  const { isSignedIn, needsOnboarding } = useAuthState()
  const [searchParams] = useSearchParams()
  const redirectTo = searchParams.get('redirectTo')

  useEffect(() => {
    const cachedUser = queryClient.getQueryData(queries.users.me.queryKey)

    if (cachedUser) {
      navigate(redirectTo ? redirectTo : paths.home.getHref(), {
        replace: true,
      })
    } else if (isSignedIn && needsOnboarding) {
      navigate(paths.auth.onboarding.getHref(redirectTo || ''), {
        replace: true,
      })
    } else if (isSignedIn && !needsOnboarding) {
      navigate(redirectTo ? redirectTo : paths.home.getHref(), {
        replace: true,
      })
    }
  }, [isSignedIn, needsOnboarding, queries.users.me.queryKey, redirectTo, navigate])

  return (
    <div
      className={cn(
        'flex',
        !isMobile && 'h-screen overflow-clip',
        isMobile ? 'h-full min-h-screen flex-col-reverse justify-between' : 'flex-row',
      )}
    >
      <AuthSidebar />
      <div
        className={cn(
          'mx-auto flex  w-full max-w-[650px] shrink-0 flex-col justify-between gap-6 overflow-y-auto py-10 ',
          isMobile ? 'max-h-none items-center' : 'max-h-[100vh] px-28',
        )}
      >
        <Link
          className={cn(isMobile && 'mb-6 self-start px-4 md:px-6')}
          to={paths.root.path}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Logo className={cn(isMobile ? 'h-8 w-32' : 'h-10 w-36')} />
        </Link>
        <Outlet />
        <div className={cn('flex flex-col gap-4', isMobile && 'px-4')}>
          {location.pathname.includes(paths.auth.signup.path) && (
            <p className="text-xs text-muted md:text-sm">
              By tapping <span className="text-primary">&apos;Agree &amp; Complete Sign Up&apos;</span>, you confirm
              that you have read and agree to our{' '}
              <a
                href={env.LINK_TO_TERMS_OF_USE}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-primary underline"
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                href={env.LINK_TO_PRIVACY_POLICY}
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-primary underline"
              >
                Privacy Policy
              </a>
            </p>
          )}
          {location.pathname.includes(paths.auth.resetPassword.confirm.path) && (
            <div className="mt-2 text-center">
              <SupportDialog
                trigger={
                  <Button className="text-sm text-muted underline" variant="ghost">
                    Need help accessing your account?
                  </Button>
                }
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
